import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { TRButton } from "@/components/TRButton";
import HeroSlider from "@/components/HeroSlider";
import { boxImage, LIFESTYLE } from "@/lib/catalog";

const STEPS = [
    {
        n: "01",
        t: "Choose Your Fit",
        d: "Low, Mid or High. Three timeless heights, nothing more.",
    },
    {
        n: "02",
        t: "Choose Your Color",
        d: "Black or White. Because timeless never goes out of style.",
    },
    {
        n: "03",
        t: "Receive Your Box",
        d: "Twenty premium pairs, ready to replace your entire sock drawer.",
    },
];

export default function Home() {
    return (
        <div data-testid="home-page">
            {/* HERO SLIDER */}
            <HeroSlider />

            {/* 3-STEP EXPLAINER */}
            <section className="cv-auto max-w-[1500px] mx-auto px-5 sm:px-8 py-24 md:py-36">
                <Reveal>
                    <span className="eyebrow text-ink/50">How it works</span>
                    <h2 className="mt-5 font-serif text-4xl md:text-5xl tracking-[-0.03em] max-w-xl">
                        Three decisions. Then never again.
                    </h2>
                </Reveal>

                <div className="mt-16 grid md:grid-cols-3 gap-px bg-line border border-line">
                    {STEPS.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.1}>
                            <div className="bg-paper p-10 md:p-12 h-full">
                                <div className="font-serif text-6xl text-ink/15">
                                    {s.n}
                                </div>
                                <h3 className="mt-8 font-serif text-2xl tracking-[-0.02em]">
                                    {s.t}
                                </h3>
                                <p className="mt-4 text-sm text-ink/60 leading-relaxed">
                                    {s.d}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <Marquee text="One box · Twenty pairs" separator="·" />

            {/* PRODUCT TEASERS */}
            <section className="cv-auto max-w-[1500px] mx-auto px-5 sm:px-8 py-24 md:py-36">
                <div className="grid md:grid-cols-2 gap-6 md:gap-10">
                    {[
                        {
                            key: "one",
                            title: "The One Box",
                            desc: "20 identical pairs of the same model and color.",
                            img: boxImage("socks", "one", "white"),
                            to: "/shop/socks/men",
                            cta: "Discover The One Box",
                        },
                        {
                            key: "starter",
                            title: "The Starter Box",
                            desc: "5 Low · 10 Mid · 5 High. Black or White.",
                            img: boxImage("socks", "starter", "black"),
                            to: "/shop/socks/women",
                            cta: "Discover The Starter Box",
                        },
                    ].map((p, i) => (
                        <Reveal key={p.key} delay={i * 0.12}>
                            <Link
                                to={p.to}
                                data-testid={`teaser-${p.key}`}
                                className="group block"
                            >
                                <div className="relative overflow-hidden border border-line bg-secondary aspect-[4/3]">
                                    <img loading="lazy" decoding="async"
                                        src={p.img}
                                        alt={p.title}
                                        className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
                                    />
                                </div>
                                <div className="mt-6 flex items-end justify-between">
                                    <div>
                                        <h3 className="font-serif text-3xl md:text-4xl tracking-[-0.03em]">
                                            {p.title}
                                        </h3>
                                        <p className="mt-2 text-sm text-ink/60">
                                            {p.desc}
                                        </p>
                                    </div>
                                    <span className="font-serif text-2xl shrink-0 pl-4">
                                        €89
                                    </span>
                                </div>
                                <span className="mt-5 inline-flex items-center gap-2 eyebrow text-ink group-hover:gap-3 transition-all">
                                    {p.cta}
                                    <ArrowRight size={14} strokeWidth={1.5} />
                                </span>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* THE TONY RAYS SYSTEM */}
            <section className="cv-auto border-t border-line bg-ink text-paper">
                <div className="max-w-[1500px] mx-auto px-5 sm:px-8 py-24 md:py-32 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
                    <Reveal>
                        <span className="eyebrow text-paper/50">The Tony Rays System</span>
                        <h2 className="mt-5 font-serif font-light text-5xl md:text-6xl tracking-[-0.04em]">
                            Not three products. One system.
                        </h2>
                        <p className="mt-6 text-paper/70 leading-relaxed max-w-md">
                            Socks, boxers and undershirts aren't separate purchases.
                            They're one complete everyday system — one color, one
                            quality, one identity. Everything matches, every single
                            day.
                        </p>
                        <ul className="mt-10 space-y-px border-t border-paper/15">
                            {[
                                { label: "Premium Socks", to: "/shop/socks/men" },
                                { label: "Premium Boxers", to: "/shop/boxers/men" },
                                { label: "Premium Undershirts", to: "/shop/undershirts/men" },
                            ].map((p) => (
                                <Link
                                    key={p.label}
                                    to={p.to}
                                    data-testid={`system-${p.label.toLowerCase().replace(/\s/g, "-")}`}
                                    className="group flex items-center justify-between py-4 border-b border-paper/15"
                                >
                                    <span className="flex items-center gap-3">
                                        <Check size={16} strokeWidth={1.5} className="text-paper/60" />
                                        <span className="font-serif text-2xl">{p.label}</span>
                                    </span>
                                    <ArrowRight
                                        size={16}
                                        strokeWidth={1.5}
                                        className="text-paper/40 group-hover:text-paper group-hover:translate-x-1 transition-all"
                                    />
                                </Link>
                            ))}
                        </ul>
                        <p className="mt-8 eyebrow text-paper/50">
                            One color · One quality · One identity
                        </p>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div className="grid grid-cols-2 gap-3">
                            <Link
                                to="/shop/socks/men"
                                data-testid="system-tile-black"
                                className="group relative border border-paper/20 overflow-hidden aspect-[3/4] bg-ink block"
                            >
                                <img loading="lazy" decoding="async"
                                    src={LIFESTYLE.men.black}
                                    alt="The Tony Rays everyday system — black"
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05] group-active:scale-[1.04]"
                                />
                                <span className="absolute left-3 bottom-3 eyebrow text-paper/80">Black</span>
                            </Link>
                            <Link
                                to="/shop/socks/men"
                                data-testid="system-tile-white"
                                className="group relative border border-paper/20 overflow-hidden aspect-[3/4] bg-secondary block"
                            >
                                <img loading="lazy" decoding="async"
                                    src={LIFESTYLE.men.white}
                                    alt="The Tony Rays everyday system — white"
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05] group-active:scale-[1.04]"
                                />
                                <span className="absolute left-3 bottom-3 eyebrow text-ink/70">White</span>
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ESSENTIAL BOX TEASER */}
            <section className="cv-auto max-w-[1500px] mx-auto px-5 sm:px-8 py-24 md:py-32">
                <Reveal>
                    <div className="grid md:grid-cols-2 border border-line">
                        <div className="p-10 md:p-16 flex flex-col justify-center order-2 md:order-1">
                            <span className="eyebrow text-ink/50">The whole philosophy · One box</span>
                            <h2 className="mt-5 font-serif text-4xl md:text-5xl tracking-[-0.03em]">
                                The Essential Box
                            </h2>
                            <p className="mt-5 text-ink/65 leading-relaxed max-w-sm">
                                5 boxers · 5 undershirts · 10 pairs of socks. Open the
                                box — your entire drawer is renewed, coordinated and
                                ready.
                            </p>
                            <div className="mt-8 flex items-center gap-6">
                                <span className="font-serif text-2xl">€89</span>
                                <TRButton as="link" to="/shop/essentials/men" data-testid="home-essentials">
                                    Discover The Essential Box
                                    <ArrowRight size={15} strokeWidth={1.5} />
                                </TRButton>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 md:gap-3 bg-secondary p-2 md:p-3 order-1 md:order-2">
                            <Link
                                to="/shop/essentials/men?color=black"
                                data-testid="essentials-tile-black"
                                className="group relative overflow-hidden bg-ink aspect-[4/5] block"
                            >
                                <img loading="lazy" decoding="async"
                                    src={boxImage("essentials", "one", "black", "men")}
                                    alt="The Essential Box — black"
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05] group-active:scale-[1.04]"
                                />
                                <span className="absolute left-3 bottom-3 eyebrow text-paper/80">Black</span>
                            </Link>
                            <Link
                                to="/shop/essentials/men?color=white"
                                data-testid="essentials-tile-white"
                                className="group relative overflow-hidden bg-paper aspect-[4/5] block"
                            >
                                <img loading="lazy" decoding="async"
                                    src={boxImage("essentials", "one", "white", "men")}
                                    alt="The Essential Box — white"
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05] group-active:scale-[1.04]"
                                />
                                <span className="absolute left-3 bottom-3 eyebrow text-ink/70">White</span>
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>

            {/* MANIFESTO STRIP */}
            <section className="cv-auto border-t border-line">
                <div className="max-w-[1500px] mx-auto px-5 sm:px-8 py-24 md:py-40">
                    <Reveal>
                        <p className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.15] tracking-[-0.03em] max-w-4xl">
                            Because real luxury isn't having more. It's needing
                            less. Own less. Live more.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <Link
                            to="/philosophy"
                            data-testid="home-philosophy-link"
                            className="mt-10 inline-flex items-center gap-2 eyebrow text-ink link-underline"
                        >
                            Read our philosophy
                            <ArrowRight size={14} strokeWidth={1.5} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
